import React, { useMemo, useContext, useState, useCallback } from 'react'
import {
  Box,
  Grid,
  MenuItem,
  Button,
  Paper,
  CircularProgress,
  Typography,
  List,
  TextField as MuiTextField,
  FormControlLabel,
  Skeleton,
  Card,
  CardContent,
  CardActions,
  FormHelperText,
  IconButton,
} from '@mui/material'
import { Form, useField, Field } from 'react-final-form'
import { TextField, Select, Checkboxes, Autocomplete } from 'mui-rff'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import { useNavigate } from 'react-router-dom'
import arrayMutators from 'final-form-arrays'
import { getSubmissionErrors } from '../../utils/getSubmissionErrors'
import { Link } from 'react-router-dom'
import { usePostStaffBenefit, usePatchStaffBenefit } from '../../api/staff-benefit'
import { StaffBenefitContext } from './StaffBenefitTable'
import { useGetAllStaff } from '../../api/user'
import { FieldArray } from 'react-final-form-arrays'
import { FormSearchUser } from './FormUserSearchComponent'
import Checkbox from '../shared/component/Checkbox'
import ClearIcon from '@mui/icons-material/Clear'
import { department } from '../../constant/department'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'

export const StaffBenefitForm = (props) => {
  const { row, setEditing } = props

  const { refetch } = useContext(StaffBenefitContext)
  const createStaffBenefit = usePostStaffBenefit()
  const editStaffBenefit = usePatchStaffBenefit()
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const [searchUserArr, setSearchUserArr] = useState([])

  const hasSearchItems = useMemo(() => {
    return !!searchUserArr?.length
  }, [searchUserArr?.length])

  const staffRes = useGetAllStaff()
  const staffData = staffRes?.data ?? []

  const handleOnSearchChange = (e) => {
    const input = document.getElementById('user-search-input')
    if (input) {
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value'
      ).set
      nativeInputValueSetter.call(input, e?.target?.value ?? '')

      const ev2 = new Event('input', { bubbles: true })
      input.dispatchEvent(ev2)
    }
  }

  const staffArray = useMemo(() => {
    return (staffData ?? []).map((staff) => {
      return {
        value: { ...staff, value: false },
        id: staff._id,
        label: `${staff.employeeId} - ${staff.name} (${staff.branch})`,
        employeeId: staff.employeeId,
        name: staff.name,
      }
    })
  }, [staffData])

  const onSubmit = (values) => {
    const startDate = moment(values.startDate)
    const endDate = moment(values.endDate)
    const { users } = values

    const selectedUsers = users
      .filter((each) => each?.value)
      .map((user) => {
        return { user: user.id }
      })

    const submitValues = {
      ...values,
      staffBenefitCode: (values?.staffBenefitCode ?? '').toUpperCase(),
      users: [...selectedUsers],
      redemptionLimit: values?.redemptionLimit ? values?.redemptionLimit : null,
      // Only set departments value when limit for department is true/checked, otherwise empty array
      departments: values.limitForDepartment ? values.departments : [],
    }

    if (endDate.isSameOrAfter(startDate)) {
      const discountPercentage = submitValues.percent / 100

      if (row?.id) {
        return editStaffBenefitCodeCall(submitValues, discountPercentage)
      } else {
        return createStaffBenefitCodeCall(submitValues, discountPercentage)
      }
    } else {
      enqueueSnackbar('End date should be after the start date', {
        variant: 'error',
      })
    }
  }

  const createStaffBenefitCodeCall = async (values, discountPercentage) => {
    const createRes = await createStaffBenefit
      .mutateAsync({ ...values, discountPercentage })
      .catch((e) => {
        if (e?.response?.data?.errors) {
          enqueueSnackbar(e?.response?.data?.errors[0]?.msg, {
            variant: 'error',
          })
        } else {
          enqueueSnackbar(e?.response?.data?.message ?? 'Fail to create', {
            variant: 'error',
          })
        }
        return getSubmissionErrors(e)
      })

    if (createRes.status === 200) {
      enqueueSnackbar('Staff benefit created', {
        variant: 'success',
      })
      navigate(`/staff-benefit`)
      return
    }
    return createRes
  }

  const editStaffBenefitCodeCall = async (values, discountPercentage) => {
    console.log(values)
    const editRes = await editStaffBenefit
      .mutateAsync({ ...values, discountPercentage, staffBenefitId: row.id })
      .catch((e) => {
        if (e?.response?.data?.errors) {
          enqueueSnackbar(e?.response?.data?.errors[0]?.msg, {
            variant: 'error',
          })
        } else {
          enqueueSnackbar(e?.response?.data?.message ?? 'Fail to update', {
            variant: 'error',
          })
        }
        return getSubmissionErrors(e)
      })
    if (editRes.status === 200) {
      enqueueSnackbar('Edit success', {
        variant: 'success',
      })
      setEditing(false)
      refetch()
      return
    }
    return editRes
  }

  const handleInitialValues = useMemo(() => {
    if (row?.id) {
      // Set values for edit
      const formattedStartDate = moment(row.startDate).format('YYYY-MM-DD')
      const formattedEndDate = moment(row.endDate).format('YYYY-MM-DD')

      const { users } = row

      const tmpUsers = staffArray?.map((each) => {
        const hasValue = !!(users ?? []).find((x) => {
          return x?.user?.id === each?.id
        })

        return { ...each, value: hasValue }
      })

      const editStaffBenefitCode = {
        ...row,
        users: tmpUsers,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        percent: +(Math.round(row.discountPercentage * 100 + 'e+2') + 'e-2'),
        search: '',
        limitForDepartment: row.departments?.length > 0 ? true : false,
      }
      return editStaffBenefitCode
    } else {
      // Set initial values for create
      const today = moment().format('YYYY-MM-DD')
      const defaultEndDate = moment(today, 'YYYY-MM-DD').add(1, 'M').format('YYYY-MM-DD')
      const tmpUsers = staffArray?.map((each) => {
        return { ...each, value: false }
      })
      const createNew = {
        startDate: today,
        endDate: defaultEndDate,
        users: tmpUsers,
        search: '',
        redemptionPerUser: 1,
        limitForDepartment: false,
        emailTerms: `<p><strong style="color: rgb(0, 0, 0);"><u>Terms and Conditions:</u></strong></p><ol><li><span style="color: rgb(0, 0, 0);">If the value of the good exceeds the amount stated in the voucher, the difference shall be paid by the bearer.</span></li><li><span style="color: rgb(0, 0, 0);">However, if the value of the goods is less than the amount stated in the voucher, the difference thereof will be forfeited. </span></li><li><span style="color: rgb(0, 0, 0);">Damaged voucher will be considered as invalid and will not be replaced. In case of lost no replacement is allowed. </span></li><li><span style="color: rgb(0, 0, 0);">This voucher is not transferable and solely for the vehicle as stated in the voucher.</span></li><li><span style="color: rgb(0, 0, 0);">Service charges and workmanship are not included.</span></li></ol>`,
      }

      return createNew
    }
  }, [row, staffArray])

  // Form validation
  const validate = (values) => {
    const startDate = moment(values.startDate)
    const endDate = moment(values.endDate)
    const errors = {}

    if (!values.name) {
      errors.name = 'Required'
    }

    if (!values.type) {
      errors.type = 'Required'
    }

    if (values.type === 'Amount') {
      if (!values.discountAmount) {
        errors.discountAmount = 'Required'
      } else if (values.discountAmount <= 0) {
        errors.discountAmount = 'Discount Amount must be greater than 0'
      }
    }

    if (values.type === 'Percentage') {
      if (!values.percent) {
        errors.percent = 'Required'
      } else if (values.percent <= 0) {
        errors.percent = 'Discount Percentage must be greater than 0'
      } else if (values.percent % 1 !== 0) {
        errors.percent = 'Decimal input are not allow'
      }
    }

    if (values.type === 'Gift' && !values.freeGift) {
      errors.freeGift = 'Required'
    }

    if (!startDate.isValid()) {
      errors.startDate = 'Date input is invalid'
    } else if (startDate.isAfter(endDate)) {
      errors.startDate = 'Start Date must be before End Date'
    }

    if (!endDate.isValid()) {
      errors.endDate = 'Date input is invalid'
    } else if (endDate.isBefore(startDate)) {
      errors.endDate = 'End Date must be after Start Date'
    }

    if (!values.limitForDepartment) {
      if (!values.redemptionPerUser) {
        errors.redemptionPerUser = 'Required'
      } else if (values.redemptionPerUser < 1) {
        errors.redemptionPerUser = 'Redemption Per User must be more than or equal to 1'
      }
    }

    errors.departments = []
    let checked = new Set()
    var hasDepDuplicate = values?.departments?.some(function (cur) {
      return checked.size === checked.add(cur.department).size
    })
    ;(values?.departments ?? [])?.forEach((dep, idx) => {
      errors.departments[idx] = {}
      if (!dep.department) {
        errors.departments[idx].department = 'Required'
      }

      if (hasDepDuplicate) {
        errors.departments[idx].department = 'Department is already existed.'
      }

      if (!dep.redemptionPerUser) {
        errors.departments[idx].redemptionPerUser = 'Required'
      }
    })

    return errors
  }

  const formFields = [
    {
      field: (
        <Box>
          <FormControlLabel
            label='Apply to New Users'
            key='applyToNewUsers'
            control={<Field name='applyToNewUsers' component={Checkbox} type='checkbox' />}
          />
          <FormHelperText>
            Enabling this will have new users to be automatically assigned to this staff benefit.
            <br></br>
            Only applicable when the end date doesn't exceed 3 months old on user create.
          </FormHelperText>
        </Box>
      ),
    },
    {
      size: 12,
      name: 'users',
      field: (
        <Box>
          <Paper style={{ padding: '0 8px', maxHeight: 264, maxWidth: 600, overflow: 'auto' }}>
            <List>
              <FieldArray name='users'>
                {({ fields }) => {
                  if (staffRes?.isLoading) {
                    return Array(10)
                      .fill()
                      .map((_, idx) => <Skeleton key={idx} width='100%' height={35} />)
                  } else if (staffArray?.length <= 0) {
                    return (
                      <Box mt={3} mb={3}>
                        <Typography>No results found</Typography>
                      </Box>
                    )
                  } else {
                    return (
                      <>
                        {fields.map((name, index) => {
                          const staff = fields.value[index]
                          const label = staff.label
                          const isInSearch =
                            !hasSearchItems ||
                            searchUserArr?.find((eachItem) => eachItem.label === label)

                          if (label && isInSearch) {
                            return (
                              <FormControlLabel
                                label={label}
                                key={name}
                                control={
                                  <Field
                                    name={`${name}.value`}
                                    component={Checkbox}
                                    type='checkbox'
                                    style={{ minWidth: '65px' }}
                                  />
                                }
                              />
                            )
                          } else {
                            return null
                          }
                        })}
                      </>
                    )
                  }
                }}
              </FieldArray>
            </List>
          </Paper>
        </Box>
      ),
    },
    {
      size: 12,
      field: <TextField label='Name' name='name' required={true} />,
    },
    {
      size: 12,
      field: <TextField label='Staff Benefit Code' name='staffBenefitCode' required={true} />,
    },
    {
      size: 6,
      field: (
        <Select
          name='type'
          label='Staff Benefit Scheme'
          formControlProps={{ margin: 'none' }}
          required={true}
        >
          <MenuItem value='Amount'>Discount (RM)</MenuItem>
          <MenuItem value='Percentage'>Discount (%)</MenuItem>
          <MenuItem value='Gift'>Free Gift</MenuItem>
        </Select>
      ),
    },
    {
      size: 12,
      when: 'type',
      is: ['Amount'],
      field: (
        <TextField
          type='number'
          label={'Discount (RM)'}
          name='discountAmount'
          required={true}
          onWheel={(e) => e.target.blur()}
        />
      ),
    },
    {
      size: 12,
      when: 'type',
      is: ['Percentage'],
      field: (
        <TextField
          type='number'
          label='Discount (%)'
          name='percent'
          required={true}
          onWheel={(e) => e.target.blur()}
        />
      ),
    },
    {
      size: 12,
      when: 'type',
      is: ['Gift'],
      field: <TextField label='Free Gift' name='freeGift' required={true} />,
    },
    {
      size: 12,
      field: <TextField label='Message' name='message' />,
    },
    {
      size: 12,
      field: (
        <Box maxWidth={290}>
          <TextField label='Start Date' type='date' name='startDate' placeholder='yyyy-mm-dd' />
        </Box>
      ),
    },
    {
      size: 12,
      field: (
        <Box maxWidth={290}>
          <TextField label='End Date' type='date' name='endDate' placeholder='yyyy-mm-dd' />
        </Box>
      ),
    },
    {
      size: 12,
      field: (
        <TextField
          label='Minimum Purchase'
          type='number'
          name='minPurchase'
          onWheel={(e) => e.target.blur()}
        />
      ),
    },
    {
      size: 12,
      when: 'type',
      is: ['Percentage'],
      field: (
        <TextField
          label={'Discount cap (RM)'}
          type='number'
          name='discountCap'
          onWheel={(e) => e.target.blur()}
        />
      ),
    },
    {
      size: 12,
      field: (
        <TextField
          type='number'
          label={'Minimum spend (RM)'}
          name='minimumSpend'
          onWheel={(e) => e.target.blur()}
        />
      ),
    },
    {
      size: 12,
      field: (
        <>
          <Typography variant='h6'>Redemption Limit</Typography>
          <Checkboxes
            name='limitForDepartment'
            color='primary'
            data={{ label: 'Redemption limit based on department?' }}
          />
        </>
      ),
    },
    {
      size: 12,
      when: 'limitForDepartment',
      is: 'false',
      field: (
        <TextField
          label='Redemption limit'
          type='number'
          name='redemptionLimit'
          onWheel={(e) => e.target.blur()}
        />
      ),
    },
    {
      size: 12,
      when: 'limitForDepartment',
      is: 'false',
      field: (
        <TextField
          label='Redemption Per User'
          type='number'
          name='redemptionPerUser'
          onWheel={(e) => e.target.blur()}
          required={true}
        />
      ),
    },
    {
      size: 12,
      when: 'limitForDepartment',
      is: 'true',
      field: (
        <FieldArray name='departments'>
          {({ fields }) => (
            <>
              {fields.map((name, index) => (
                <Box
                  display='flex'
                  justifyContent='space-between'
                  flexDirection='row'
                  p='8px'
                  border='1px solid #e0e0e0'
                  borderRadius={1}
                >
                  <Box
                    display='flex'
                    flexDirection={{ xs: 'column', sm: 'row' }}
                    gap='8px'
                    marginBottom='8px'
                    width='100%'
                  >
                    <Autocomplete
                      fullWidth
                      name={`${name}.department`}
                      label='Department'
                      variant='outlined'
                      textFieldProps={{ variant: 'outlined' }}
                      options={Object.keys(department)}
                      getOptionLabel={(site) => site}
                      va
                    />
                    <TextField
                      label='Redemption Per User'
                      type='number'
                      name={`${name}.redemptionPerUser`}
                      onWheel={(e) => e.target.blur()}
                    />
                    <TextField
                      label='Redemption Per Department'
                      type='number'
                      name={`${name}.redemptionPerDepartment`}
                      onWheel={(e) => e.target.blur()}
                    />
                  </Box>
                  <IconButton onClick={() => fields.remove(index)}>
                    <ClearIcon />
                  </IconButton>
                </Box>
              ))}
              <Box my={1}>
                <Button
                  variant='outlined'
                  onClick={() =>
                    fields.push({
                      department: undefined,
                      redemptionPerUser: undefined,
                      redemptionPerDepartment: undefined,
                    })
                  }
                >
                  Add
                </Button>
              </Box>
            </>
          )}
        </FieldArray>
      ),
    },
    {
      size: 12,
      field: (
        <>
          <Typography variant='h6'>Terms (For email)</Typography>

          <Field name='emailTerms'>
            {({ input: { value, ...input } }) => (
              <ReactQuill
                theme='snow'
                value={value}
                onChange={(newValue, delta, source) => {
                  if (source === 'user') {
                    input.onChange(newValue)
                  }
                }}
              />
            )}
          </Field>
        </>
      ),
    },
  ]

  return (
    <Box>
      <FormSearchUser setSearchArr={setSearchUserArr} userArray={staffArray} />
      <Form
        onSubmit={onSubmit}
        mutators={{
          ...arrayMutators,
        }}
        validate={validate}
        keepDirtyOnReinitialize //let data stay on the form
        initialValues={handleInitialValues}
        component={({ handleSubmit, submitting, values, form }) => {
          const handleSelectAllUsers = () => {
            if (staffData) {
              const allStaff = values?.users?.map((user) => {
                if (hasSearchItems) {
                  const isFormUsers = searchUserArr?.find((each) => each.id === user.id)
                  if (isFormUsers) {
                    return { ...user, value: true }
                  } else {
                    return { ...user }
                  }
                } else {
                  const isFormUsers = staffArray?.find((each) => each.id === user.id)
                  if (isFormUsers) {
                    return { ...user, value: true }
                  } else {
                    return { ...user }
                  }
                }
              })
              form.change('users', allStaff)
            }
          }

          const handleDeselectAllUsers = () => {
            if (staffData) {
              const allStaff = values?.users?.map((user) => {
                if (hasSearchItems) {
                  const isFormUsers = searchUserArr?.find((each) => each.id === user.id)
                  if (isFormUsers) {
                    return { ...user, value: false }
                  } else {
                    return { ...user }
                  }
                } else {
                  const isFormUsers = staffArray?.find((each) => each.id === user._id)
                  if (isFormUsers) {
                    return { ...user, value: false }
                  } else {
                    return { ...user }
                  }
                }
              })
              form.change('users', allStaff)
            }
          }

          const curFormUsers = values?.users?.filter((user) => {
            if (hasSearchItems) {
              return searchUserArr?.find((each) => each.id === user.id)
            } else {
              return staffArray?.find((each) => each.id === user.id)
            }
          })

          return (
            <Box>
              <form onSubmit={handleSubmit} noValidate>
                <Card>
                  <CardContent>
                    <Grid container spacing={4}>
                      {formFields.map((item, idx) => {
                        return (
                          <RenderItem
                            key={idx}
                            idx={idx}
                            handleOnSearchChange={handleOnSearchChange}
                            item={item}
                            handleSelectAllUsers={handleSelectAllUsers}
                            handleDeselectAllUsers={handleDeselectAllUsers}
                            curFormUsers={curFormUsers}
                          />
                        )
                      })}
                    </Grid>
                  </CardContent>
                  <CardActions>
                    <Box p={1} mb={1} width='100%' display='flex' justifyContent='flex-end'>
                      {row ? (
                        <Button variant='outlined' onClick={() => setEditing(false)}>
                          Cancel
                        </Button>
                      ) : (
                        <Link
                          style={{ textDecoration: 'none', color: 'black' }}
                          to='/staff-benefit'
                        >
                          <Button variant='outlined'> Back</Button>
                        </Link>
                      )}

                      <Button
                        variant='contained'
                        type='submit'
                        disabled={
                          submitting || editStaffBenefit.isLoading || createStaffBenefit.isLoading
                        }
                        color='primary'
                        style={{ marginLeft: 10 }}
                      >
                        {editStaffBenefit.isLoading || createStaffBenefit.isLoading ? (
                          <Box mr={1}>
                            <CircularProgress size='1.5rem' />
                          </Box>
                        ) : (
                          ''
                        )}
                        {row ? 'Save' : 'Create'}
                      </Button>
                    </Box>
                  </CardActions>
                </Card>
              </form>
            </Box>
          )
        }}
      />
    </Box>
  )
}

const RenderItem = ({
  item,
  handleSelectAllUsers,
  handleDeselectAllUsers,
  curFormUsers,
  idx,
  handleOnSearchChange,
}) => {
  const {
    input: { value },
  } = useField(item.when, { subscription: { value: true } })

  if (!(item.when || item.is) || item?.is?.includes(value)) {
    if (item.name === 'users') {
      return (
        /* Search users */
        <Grid item xs={12} sm={item.size} key={idx}>
          <Box mb={1}>
            <Typography variant='h6'>Specify Users</Typography>
          </Box>
          <Box
            display='flex'
            alignItems='center'
            mb={1}
            style={{ width: '100%', maxWidth: '600px' }}
          >
            <Box mr={2} style={{ width: '100%', maxWidth: '468px' }}>
              <Field name='search'>
                {(props) => {
                  const combinedOnChange = (e) => {
                    props.input.onChange(e)
                    handleOnSearchChange(e)
                  }
                  return (
                    <MuiTextField
                      label='Search'
                      name={props.input.name}
                      value={props.input.value}
                      onChange={combinedOnChange}
                      fullWidth={true}
                    />
                  )
                }}
              </Field>
            </Box>
            {curFormUsers?.some((e) => e.value === true) ? (
              <Button variant='outlined' onClick={handleDeselectAllUsers}>
                Deselect All
              </Button>
            ) : (
              <Button
                variant='contained'
                style={{ backgroundColor: 'black', color: 'white' }}
                onClick={handleSelectAllUsers}
              >
                Select All
              </Button>
            )}
          </Box>
          {item.field}
        </Grid>
      )
    }

    return (
      <Grid item xs={12} sm={item.size} key={idx}>
        {item.field}
      </Grid>
    )
  }
  return null
}
